// src/stores/ViolationStore.ts - Phiên bản cập nhật

import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './index';
import violationService from '@/services/violationService';
import {
    ViolationReportsParams,
    WorkSearchParams,
    ViolationStats,
    ViolationReportFormData,
    ViolationReportFormState,
    WorkDisplayData,
    ViolationReportWithDetails,
    PaginatedResponse,
    ID,
    ContentReport,
    Platform,
    Violation
} from '@/types';

const initialReportFormData: ViolationReportFormData = {
    workId: '',
    violationType: 'other',
    violationUrl: '',
    platform: '',
    description: '',
    declaration: false,
    evidenceFiles: [],
    identityProtection: false,
    fingerprint: null
};

class ViolationStore {
    rootStore: RootStore;

    // Works data
    myWorks: WorkDisplayData[] = [];
    isLoadingWorks: boolean = false;
    worksPagination: PaginatedResponse<WorkDisplayData>['pagination'] = {
        page: 1,
        pageSize: 20,
        pageCount: 1,
        total: 0
    };

    // Search results
    searchResults: WorkDisplayData[] = [];
    isSearching: boolean = false;

    // Report form data - sử dụng interface từ types
    reportFormData: Omit<ViolationReportFormState, 'currentStep' | 'isValid' | 'errors'> = {
        ...initialReportFormData
    };

    // Submission state
    isSubmitting: boolean = false;
    submitSuccess: boolean = false;

    // Reports data
    myReports: ViolationReportWithDetails[] = [];
    isLoadingReports: boolean = false;
    reportsPagination: PaginatedResponse<ViolationReportWithDetails>['pagination'] = {
        page: 1,
        pageSize: 20,
        pageCount: 1,
        total: 0
    };

    violationDetail: Violation | null = null;

    // Stats data
    violationStats: ViolationStats | null = null;
    isLoadingStats: boolean = false;

    // Current filters
    reportsFilters: ViolationReportsParams = {
        page: 1,
        limit: 20,
        search: '',
        violationStatus: 'all',
        violationType: 'all',
        platform: 'all',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    };

    // Error handling
    error: string | null = null;

    // Platform info
    supportedPlatforms: any[] = [];

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // Reset error
    clearError() {
        this.error = null;
    }

    // Set loading state
    setLoadingWorks(loading: boolean) {
        this.isLoadingWorks = loading;
    }

    setLoadingReports(loading: boolean) {
        this.isLoadingReports = loading;
    }

    setLoadingStats(loading: boolean) {
        this.isLoadingStats = loading;
    }

    // Form data management
    setFormData<K extends keyof ViolationReportFormData>(field: K, value: ViolationReportFormData[K]) {
        (this.reportFormData as any)[field] = value;
    }

    resetFormData() {
        this.reportFormData = {
            ...initialReportFormData
        };
        this.submitSuccess = false;
    }

    // Auto-detect platform from URL
    detectPlatformFromUrl(url: string) {
        const results = violationService.detectPlatformFromUrl(url);
        return results;
    }

    // Search user works với autocomplete
    async searchWorks(params: WorkSearchParams) {
        this.isSearching = true;
        this.error = null;

        try {
            const results = await violationService.searchWorks(params);

            runInAction(() => {
                this.searchResults = results;
                this.isSearching = false;
            });

            return results;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tìm kiếm tác phẩm';
                this.isSearching = false;
                this.searchResults = [];
            });

            throw error;
        }
    }

    // Fetch user works với pagination
    async fetchMyWorks(page = 1, limit = 20) {
        this.setLoadingWorks(true);
        this.error = null;

        try {
            const response = await violationService.getUserWorks(page, limit);

            runInAction(() => {
                this.myWorks = response.data;
                this.worksPagination = response.pagination;
                this.setLoadingWorks(false);
            });

            return response;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải danh sách tác phẩm';
                this.setLoadingWorks(false);

                // Fallback data cho development
                if (import.meta.env.MODE === 'development') {
                    this.myWorks = [
                        {
                            id: 1,
                            title: 'Bộ ảnh Hà Nội mùa thu',
                            description: 'Bộ sưu tập ảnh chụp Hà Nội vào mùa thu',
                            contentKind: 'image',
                            category: 'Nhiếp ảnh',
                            registrationDate: '2023-10-01T00:00:00Z'
                        },
                        {
                            id: 2,
                            title: 'Video du lịch Sapa',
                            description: 'Video quảng bá du lịch Sapa',
                            contentKind: 'video',
                            category: 'Video',
                            registrationDate: '2023-09-15T00:00:00Z'
                        }
                    ];
                }
            });

            throw error;
        }
    }

    // Submit violation report
    async submitViolationReport(data?: ViolationReportFormData) {
        const reportData = data || this.reportFormData;

        this.isSubmitting = true;
        this.error = null;

        try {
            await violationService.reportViolation({
                workId: reportData.workId,
                violationType: reportData.violationType,
                violationUrl: reportData.violationUrl,
                platform: reportData.platform,
                description: reportData.description,
                declaration: reportData.declaration,
                evidenceFiles: reportData.evidenceFiles,
                identityProtection: reportData.identityProtection,
                fingerprint: reportData.fingerprint,
            });

            runInAction(() => {
                this.submitSuccess = true;
                this.isSubmitting = false;
            });

            // Refresh reports list
            await this.fetchReports();

        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể gửi báo cáo vi phạm';
                this.isSubmitting = false;
            });

            throw error;
        }
    }

    // Update reports filters
    updateReportsFilters(filters: Partial<ViolationReportsParams>) {
        this.reportsFilters = { ...this.reportsFilters, ...filters };
    }

    async fetchMyReports(params?: ViolationReportsParams) {
        const searchParams = params || this.reportsFilters;
        searchParams.userId = this.rootStore?.authStore?.currentUser?.id

        return this.fetchReports(searchParams);
    }

    // Fetch user reports với filtering
    async fetchReports(params?: ViolationReportsParams) {        
        const searchParams = params || this.reportsFilters;

        if(!params) {
            delete searchParams?.userId
        }

        this.setLoadingReports(true);
        this.error = null;

        try {
        const searchParams = params || this.reportsFilters;
            const response = await violationService.getUserViolationReports(searchParams);

            runInAction(() => {
                this.myReports = response.data;
                this.reportsPagination = response.pagination;
                this.setLoadingReports(false);
            });

            return response;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải danh sách báo cáo';
                this.setLoadingReports(false);

                // Fallback data cho development
                if (import.meta.env.MODE === 'development') {
                    this.myReports = [
                        {
                            id: 1,
                            violationType: 'unauthorized_usage',
                            violationUrl: 'https://example.com/violation',
                            platform: 'facebook',
                            description: 'Sử dụng hình ảnh của tôi mà không được phép',
                            status: 'pending',
                            reportDate: '2023-10-20T10:30:00Z',
                            createdAt: '2023-10-20T10:30:00Z',
                            content: {
                                id: 1,
                                title: 'Bộ ảnh Hà Nội mùa thu',
                                contentKind: 'image'
                            },
                            evidences: []
                        }
                    ];
                    this.reportsPagination = {
                        page: 1,
                        pageSize: 20,
                        pageCount: 1,
                        total: 1
                    };
                }
            });

            throw error;
        }
    }

    // Fetch violation stats
    async fetchViolationStats(timeRange: 'week' | 'month' | 'quarter' | 'year' = 'month') {
        this.setLoadingStats(true);
        this.error = null;

        try {
            const stats = await violationService.getViolationStats(timeRange);

            runInAction(() => {
                this.violationStats = stats;
                this.setLoadingStats(false);
            });

            return stats;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải thống kê vi phạm';
                this.setLoadingStats(false);
            });

            throw error;
        }
    }

    // Get violation report detail
    async getViolationReportDetail(reportId: ID) {
        this.isLoadingReports = true;
        this.error = null;

        try {
            const report = await violationService.getViolationReport(reportId);

            runInAction(() => {
                this.violationDetail = report;
            });

            return report;
        } catch (error: any) {
            runInAction(() => {
                this.error = error.response?.data?.error?.message || 'Không thể tải chi tiết báo cáo';
            });

            throw error;
        } finally {
            this.isLoadingReports = false;
        }
    }

    // Update violation report status (for admin/legal team)
    async updateReportStatus(reportId: ID, status: string, resolution?: string) {
        try {
            await violationService.updateViolationReportStatus(reportId, status, resolution);

            // Refresh reports list
            await this.fetchReports();

            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error?.message || 'Không thể cập nhật trạng thái báo cáo';
            throw error;
        }
    }

    // Delete violation report
    async deleteViolationReport(reportId: number) {
        try {
            await violationService.deleteViolationReport(reportId);

            // Remove from local state
            runInAction(() => {
                this.myReports = this.myReports.filter(report => report.id !== reportId);
            });

            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error?.message || 'Không thể xóa báo cáo';
            throw error;
        }
    }

    // Load supported platforms
    async loadSupportedPlatforms() {
        try {
            const platforms = await violationService.getSupportedPlatforms();

            runInAction(() => {
                this.supportedPlatforms = platforms;
            });

            return platforms;
        } catch (error: any) {
            console.error('Error loading supported platforms:', error);

            // Fallback platforms
            runInAction(() => {
                this.supportedPlatforms = [
                    { id: 'facebook', name: 'Facebook', icon: 'fab fa-facebook' },
                    { id: 'instagram', name: 'Instagram', icon: 'fab fa-instagram' },
                    { id: 'youtube', name: 'YouTube', icon: 'fab fa-youtube' },
                    { id: 'tiktok', name: 'TikTok', icon: 'fab fa-tiktok' },
                    { id: 'website', name: 'Website', icon: 'fas fa-globe' }
                ];
            });
        }
    }

    // Computed properties
    get totalReports() {
        return this.reportsPagination.total;
    }

    get pendingReportsCount() {
        return this.myReports.filter(report => report.violationStatus === 'pending').length;
    }

    get resolvedReportsCount() {
        return this.myReports.filter(report => report.violationStatus === 'resolved').length;
    }

    get hasActiveFilters() {
        const { search, violationStatus, violationType, platform } = this.reportsFilters;
        return !!(search || (violationStatus && violationStatus !== 'all') ||
            (violationType && violationType !== 'all') ||
            (platform && platform !== 'all'));
    }

    get isFormValid() {
        return !!(
            this.reportFormData.workId &&
            this.reportFormData.violationType &&
            this.reportFormData.violationUrl &&
            this.reportFormData.description &&
            this.reportFormData.declaration
        );
    }
}

export default ViolationStore;